import { useState } from 'react';
import { Cascader, Input, Row, Col } from 'antd';
import { useIntl, defineMessages } from 'react-intl';
import { getParentContainer } from '@/utils/util';
import { areaCountryList, AreaLgName } from '@/constants';
import useLanguage from '@/hooks/useLanguage';
import { DefaultOptionType } from 'antd/es/select';
import MobileCountryPicker, { type MobileCountryPath } from '@/components/form/MobileCountryPicker';

export type PhoneNumberSelectValue = {
  countryId: number;
  phoneCode: string;
  phoneNumber: string;
};

interface PhoneNumberSelectProps {
  areaCountryOpts?: DefaultOptionType[];
  onChange?(value?: Partial<PhoneNumberSelectValue>): void;
  value?: Partial<PhoneNumberSelectValue>;
  onBlur?(): void;
  onValidate?(): void;
  screenWidth: number;
  isMobile?: boolean;
}
export const getCountryByAreaNameAndCountryId = ([areaName, countryId]: [string, number], areaKey: keyof AreaLgName) => {
  const area = areaCountryList.find((area) => area[areaKey] === areaName);
  if (!area) return null;

  const country = area.countryList.find((country) => country.countryId === countryId);
  if (!country) return null;

  return country;
};

export const getAreaByCountryId = (countryId: number) => {
  for (let i = 0, len = areaCountryList.length; i < len; i++) {
    const country = areaCountryList[i].countryList.find((country) => country.countryId === countryId);
    if (country) {
      return areaCountryList[i];
    }
  }
};
const placeholders = defineMessages({
  phoneNumberCountryPlaceholder: {
    defaultMessage: 'Please select your country or region',
  },
  phoneNumberDetailPlaceholder: {
    defaultMessage: 'Please enter',
  },
});

const PhoneNumberSelect: React.FC<PhoneNumberSelectProps> = ({ areaCountryOpts, onChange, value, onBlur, onValidate, screenWidth, isMobile }) => {
  const { formatMessage, locale } = useIntl();
  const [phoneCode, setPhoneCode] = useState('');

  const { getI18nValue } = useLanguage();
  const handleChange: PhoneNumberSelectProps['onChange'] = (newValue) => {
    onChange && onChange({ ...value, ...newValue });
  };

  const handleCountryChange = (value?: MobileCountryPath | []) => {
    const country = value?.length === 2 ? getCountryByAreaNameAndCountryId(value as [string, number], getI18nValue('areaCountryOneMap')) : null;
    setPhoneCode(country?.phoneCode || '');
    handleChange({
      phoneCode: country?.phoneCode,
      countryId: country?.countryId,
    });
    onValidate && onValidate();
  };

  const getCountryValue = (): MobileCountryPath | undefined => {
    if (!value || !value.countryId) return;
    const area = getAreaByCountryId(value.countryId)!;
    const areaKey = getI18nValue('areaCountryOneMap');
    return [area[areaKey], value.countryId];
  };

  return (
    <Row gutter={8} className="flex items-center" style={{ gap: screenWidth < 554 && screenWidth > 350 ? 12 : 0 }}>
      <Col span={screenWidth < 554 && screenWidth > 350 ? 24 : 12}>
        {isMobile ? (
          <MobileCountryPicker
            value={getCountryValue()}
            showPhoneCode
            placeholder={formatMessage(placeholders.phoneNumberCountryPlaceholder)}
            onChange={handleCountryChange}
            onBlur={onBlur}
          />
        ) : (
          <Cascader
            value={getCountryValue()}
            displayRender={(label) => label[label.length - 1]}
            options={areaCountryOpts}
            placeholder={formatMessage(placeholders.phoneNumberCountryPlaceholder)}
            getPopupContainer={getParentContainer}
            onChange={(nextValue) => handleCountryChange(nextValue as MobileCountryPath | [])}
            onBlur={onBlur}
          />
        )}
      </Col>
      <Col span={screenWidth < 554 && screenWidth > 350 ? 24 : 12}>
        <Input
          allowClear
          value={value?.phoneNumber}
          placeholder={formatMessage(placeholders.phoneNumberDetailPlaceholder)}
          maxLength={15}
          prefix={
            phoneCode ? (
              <span
                style={{
                  borderRight: '1px solid #D9D9D9',
                  paddingRight: 12,
                  marginRight: 8,
                }}
              >
                {phoneCode}
              </span>
            ) : null
          }
          onChange={(e) => handleChange({ phoneNumber: e.target.value })}
          onBlur={onBlur}
        />
      </Col>
    </Row>
  );
};

export default PhoneNumberSelect;
